// Binary downloads (paystub PDFs, photo ZIPs). `request()` in http.js parses
// text, so a blob has to be fetched with the same base and headers and handed
// to the browser here. Shared by Field (a worker's own paystub) and Office.

import { API_BASE, authHeaders, HttpError, NetworkError } from './http';

export async function downloadBlob(path, filename) {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  } catch (e) {
    throw new NetworkError(path, e);
  }
  if (!res.ok) {
    let body = null;
    try { body = JSON.parse(await res.text()); } catch (_) { body = null; }
    throw new HttpError(res.status, body, path);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}

/** Copy text to the clipboard; false when the browser refuses. */
export async function copyText(text) {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* fall through */ }
  return false;
}

/**
 * The path and filename for one paystub PDF.
 *
 * ── Why `approved_only=true` is not optional here (2026-09-10) ─────────────
 *
 * `GET /api/time-entries/paystub/:userId` picks its lines three ways
 * (routes/timeEntries.js:757-786), in this order:
 *
 *   1. the paystub's own timesheet, when `paystub_id` names a stub that has
 *      one — which is every stub generated from an approved week;
 *   2. APPROVED timesheet entries in the period, when `approved_only=true`;
 *   3. otherwise the RAW CLOCK — `time_entries`, whatever anybody happened to
 *      punch, approved or not.
 *
 * This helper only ever sent `paystub_id`, so it landed on (1) for a generated
 * stub and fell through to (3) for a HAND-ENTERED one — the corrections, the
 * mid-period hires, the late weeks, which is exactly the population most
 * likely to have unapproved clock time sitting behind it. The document a
 * worker was handed for those was built from hours nobody had approved, while
 * Payroll's own single download for the same endpoint sent `approved_only=true`
 * and produced a different one. Two documents, one product.
 *
 * The same defect had already been found and fixed one screen over — see the
 * `crew-paystub-approved` scenario in scripts/ui-check.js — and this was the
 * third caller, still on the old path. It changes nothing for a generated
 * paystub: branch (1) is chosen before the flag is read.
 */
export function paystubPdf(userId, paystub, name) {
  const slug = String(name || 'paystub').replace(/\s+/g, '-').toLowerCase();
  return {
    path: `/time-entries/paystub/${userId}?paystub_id=${encodeURIComponent(paystub.id)}&approved_only=true`,
    filename: `paystub-${slug}-${paystub.pay_period_start}.pdf`,
  };
}
